"use client";

import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Clock,
  Edit3,
  Image as ImageIcon,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";

import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";

import {
  fireDB,
  storage,
} from "../../../../Backend/Firebase/FirebaseConfigData";

/* =========================================================
   TYPES
========================================================= */

type ImageStatus =
  | "Active"
  | "Scheduled"
  | "Expired"
  | "Disabled";

type ImageItem = {
  id: string;
  title: string;
  imageUrl: string;
  storagePath: string;

  enabled: boolean;

  startAt: Timestamp;
  endAt: Timestamp;

  status: ImageStatus;

  createdAt?: Timestamp;
  updatedAt?: Timestamp;
};

/* =========================================================
   HELPERS
========================================================= */

function getStatus(
  enabled: boolean,
  startAt: Timestamp,
  endAt: Timestamp
): ImageStatus {
  if (!enabled) {
    return "Disabled";
  }

  const now = Date.now();

  const start = startAt.toMillis();
  const end = endAt.toMillis();

  if (now < start) {
    return "Scheduled";
  }

  if (now > end) {
    return "Expired";
  }

  return "Active";
}

function timestampToDate(timestamp: Timestamp) {
  return timestamp.toDate();
}

function formatDate(timestamp: Timestamp) {
  return timestampToDate(timestamp).toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
}

function formatTime(timestamp: Timestamp) {
  return timestampToDate(timestamp).toLocaleTimeString(
    "en-IN",
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }
  );
}

function createTimestamp(
  date: string,
  time: string
) {
  return Timestamp.fromDate(
    new Date(`${date}T${time}`)
  );
}

/* =========================================================
   PAGE
========================================================= */

export default function AdminImagesPage() {
  const [images, setImages] = useState<ImageItem[]>([]);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const [editingImage, setEditingImage] =
    useState<ImageItem | null>(null);

  const [search, setSearch] = useState("");

  const [selectedFile, setSelectedFile] =
    useState<File | null>(null);

  const [preview, setPreview] = useState("");

  const [form, setForm] = useState({
    title: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    enabled: true,
  });

  /* =========================================================
     FETCH IMAGES
  ========================================================= */

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);

      const imagesRef = collection(
        fireDB,
        "scheduledImages"
      );

      const imagesQuery = query(
        imagesRef,
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(imagesQuery);

      const data: ImageItem[] = [];

      snapshot.forEach((document) => {
        const item = document.data();

        if (
          !item.startAt ||
          !item.endAt
        ) {
          return;
        }

        const startAt =
          item.startAt as Timestamp;

        const endAt =
          item.endAt as Timestamp;

        data.push({
          id: document.id,

          title: item.title || "",

          imageUrl:
            item.imageUrl || "",

          storagePath:
            item.storagePath || "",

          enabled:
            item.enabled !== false,

          startAt,

          endAt,

          status: getStatus(
            item.enabled !== false,
            startAt,
            endAt
          ),

          createdAt:
            item.createdAt,

          updatedAt:
            item.updatedAt,
        });
      });

      setImages(data);
    } catch (error) {
      console.error(
        "Failed to fetch images:",
        error
      );

      alert(
        "Failed to load images from Firebase."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     REFRESH STATUSES
  ========================================================= */

  useEffect(() => {
    const interval = setInterval(() => {
      setImages((current) =>
        current.map((image) => ({
          ...image,
          status: getStatus(
            image.enabled,
            image.startAt,
            image.endAt
          ),
        }))
      );
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  /* =========================================================
     FILTER
  ========================================================= */

  const filteredImages = useMemo(() => {
    return images.filter((image) =>
      image.title
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [images, search]);

  /* =========================================================
     OPEN ADD
  ========================================================= */

  const openAddModal = () => {
    setEditingImage(null);

    setSelectedFile(null);

    setPreview("");

    setForm({
      title: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      enabled: true,
    });

    setShowModal(true);
  };

  /* =========================================================
     OPEN EDIT
  ========================================================= */

  const openEditModal = (
    image: ImageItem
  ) => {
    setEditingImage(image);

    setSelectedFile(null);

    setPreview(image.imageUrl);

    const start =
      timestampToDate(image.startAt);

    const end =
      timestampToDate(image.endAt);

    setForm({
      title: image.title,

      startDate:
        start.toISOString().split("T")[0],

      startTime:
        start.toTimeString().slice(0, 5),

      endDate:
        end.toISOString().split("T")[0],

      endTime:
        end.toTimeString().slice(0, 5),

      enabled: image.enabled,
    });

    setShowModal(true);
  };

  /* =========================================================
     IMAGE SELECT
  ========================================================= */

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert(
        "Please select an image file."
      );

      return;
    }

    setSelectedFile(file);

    const imageUrl =
      URL.createObjectURL(file);

    setPreview(imageUrl);
  };

  /* =========================================================
     SAVE IMAGE
  ========================================================= */

  const handleSave = async () => {
  console.log("========== SAVE START ==========");

  if (!form.title.trim()) {
    alert("Please enter a title.");
    return;
  }

  if (!editingImage && !selectedFile) {
    alert("Please select an image.");
    return;
  }

  if (!preview) {
    alert("Please select an image.");
    return;
  }

  if (!form.startDate || !form.startTime) {
    alert("Please select the start date and time.");
    return;
  }

  if (!form.endDate || !form.endTime) {
    alert("Please select the end date and time.");
    return;
  }

  const start = new Date(
    `${form.startDate}T${form.startTime}`
  );

  const end = new Date(
    `${form.endDate}T${form.endTime}`
  );

  if (end <= start) {
    alert("End date/time must be after start date/time.");
    return;
  }

  try {
    setSaving(true);

    console.log("STEP 1: Validation passed");

    const startAt = createTimestamp(
      form.startDate,
      form.startTime
    );

    const endAt = createTimestamp(
      form.endDate,
      form.endTime
    );

    console.log("STEP 2: Timestamps created");

    /* =====================================================
       EDIT
    ===================================================== */

    if (editingImage) {
      console.log("STEP 3: Editing existing image");

      let imageUrl = editingImage.imageUrl;
      let storagePath = editingImage.storagePath;

      /*
       * Upload new image ONLY if user selected one.
       */

      if (selectedFile) {
        console.log(
          "STEP 4: New file selected:",
          selectedFile.name
        );

        const newStoragePath =
          `scheduled-images/${Date.now()}-${selectedFile.name}`;

        console.log(
          "STEP 5: Storage path:",
          newStoragePath
        );

        const storageRef = ref(
          storage,
          newStoragePath
        );

        console.log(
          "STEP 6: Starting upload..."
        );

        await uploadBytes(
          storageRef,
          selectedFile
        );

        console.log(
          "STEP 7: Upload completed"
        );

        console.log(
          "STEP 8: Getting download URL..."
        );

        imageUrl =
          await getDownloadURL(
            storageRef
          );

        console.log(
          "STEP 9: Download URL:",
          imageUrl
        );

        storagePath =
          newStoragePath;

        /*
         * Delete old image.
         */

        if (editingImage.storagePath) {
          console.log(
            "STEP 10: Deleting old image..."
          );

          try {
            const oldRef = ref(
              storage,
              editingImage.storagePath
            );

            await deleteObject(
              oldRef
            );

            console.log(
              "STEP 11: Old image deleted"
            );
          } catch (error) {
            console.warn(
              "Old image deletion failed:",
              error
            );
          }
        }
      }

      console.log(
        "STEP 12: Updating Firestore..."
      );

      const imageRef = doc(
        fireDB,
        "scheduledImages",
        editingImage.id
      );

      await updateDoc(imageRef, {
        title: form.title.trim(),
        imageUrl,
        storagePath,
        enabled: form.enabled,
        startAt,
        endAt,
        updatedAt: serverTimestamp(),
      });

      console.log(
        "STEP 13: Firestore update completed"
      );
    }

    /* =====================================================
       CREATE
    ===================================================== */

    else {
      console.log(
        "STEP 3: Creating new image"
      );

      if (!selectedFile) {
        alert("Please select an image.");
        return;
      }

      const storagePath =
        `scheduled-images/${Date.now()}-${selectedFile.name}`;

      console.log(
        "STEP 4: Storage path:",
        storagePath
      );

      const storageRef = ref(
        storage,
        storagePath
      );

      console.log(
        "STEP 5: Starting Firebase Storage upload..."
      );

      await uploadBytes(
        storageRef,
        selectedFile
      );

      console.log(
        "STEP 6: Firebase Storage upload completed"
      );

      console.log(
        "STEP 7: Getting download URL..."
      );

      const imageUrl =
        await getDownloadURL(
          storageRef
        );

      console.log(
        "STEP 8: Download URL received:",
        imageUrl
      );

      console.log(
        "STEP 9: Creating Firestore document..."
      );

      await addDoc(
        collection(
          fireDB,
          "scheduledImages"
        ),
        {
          title: form.title.trim(),

          imageUrl,

          storagePath,

          enabled: form.enabled,

          startAt,

          endAt,

          createdAt:
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),
        }
      );

      console.log(
        "STEP 10: Firestore document created"
      );
    }

    console.log(
      "STEP 11: Refreshing image list..."
    );

    await fetchImages();

    console.log(
      "STEP 12: Images refreshed"
    );

    setShowModal(false);

    setEditingImage(null);

    setSelectedFile(null);

    setPreview("");

    alert(
      editingImage
        ? "Image updated successfully."
        : "Image uploaded successfully."
    );

    console.log(
      "========== SAVE COMPLETE =========="
    );
  } catch (error) {
    console.error(
      "========== SAVE ERROR ==========",
      error
    );

    console.error(
      "Firebase error details:",
      JSON.stringify(
        error,
        Object.getOwnPropertyNames(error)
      )
    );

    alert(
      `Failed to save image: ${
        error instanceof Error
          ? error.message
          : "Unknown error"
      }`
    );
  } finally {
    console.log(
      "========== SETTING SAVING FALSE =========="
    );

    setSaving(false);
  }
};

  /* =========================================================
     DELETE IMAGE
  ========================================================= */

  const deleteImage = async (
    image: ImageItem
  ) => {
    const confirmed =
      window.confirm(
        "Are you sure you want to delete this image?"
      );

    if (!confirmed) return;

    try {
      /*
       * Delete Storage file
       */

      if (image.storagePath) {
        try {
          const storageRef = ref(
            storage,
            image.storagePath
          );

          await deleteObject(
            storageRef
          );
        } catch (error) {
          console.warn(
            "Storage file could not be deleted:",
            error
          );
        }
      }

      /*
       * Delete Firestore document
       */

      await deleteDoc(
        doc(
          fireDB,
          "scheduledImages",
          image.id
        )
      );

      setImages((current) =>
        current.filter(
          (item) =>
            item.id !== image.id
        )
      );

      alert(
        "Image deleted successfully."
      );
    } catch (error) {
      console.error(
        "Delete error:",
        error
      );

      alert(
        "Failed to delete image."
      );
    }
  };

  /* =========================================================
     ENABLE / DISABLE
  ========================================================= */

  const toggleStatus = async (
    image: ImageItem
  ) => {
    try {
      const newEnabled =
        !image.enabled;

      /*
       * Update Firestore
       */

      await updateDoc(
        doc(
          fireDB,
          "scheduledImages",
          image.id
        ),
        {
          enabled: newEnabled,
          updatedAt:
            serverTimestamp(),
        }
      );

      /*
       * Update local state
       */

      setImages((current) =>
        current.map((item) => {
          if (
            item.id !== image.id
          ) {
            return item;
          }

          return {
            ...item,

            enabled: newEnabled,

            status: getStatus(
              newEnabled,
              item.startAt,
              item.endAt
            ),
          };
        })
      );
    } catch (error) {
      console.error(
        "Toggle error:",
        error
      );

      alert(
        "Failed to update image status."
      );
    }
  };

  /* =========================================================
     CLOSE MODAL
  ========================================================= */

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);

    setEditingImage(null);

    setSelectedFile(null);

    setPreview("");
  };

  /* =========================================================
     STATS
  ========================================================= */

  const totalImages =
    images.length;

  const activeImages =
    images.filter(
      (image) =>
        image.status === "Active"
    ).length;

  const scheduledImages =
    images.filter(
      (image) =>
        image.status === "Scheduled"
    ).length;

  const disabledImages =
    images.filter(
      (image) =>
        image.status === "Disabled"
    ).length;

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="min-h-screen bg-[#f7f8fa] p-4 sm:p-6 lg:p-8 dark:bg-[#0b0d10]">
      <div className="mx-auto max-w-7xl">

        {/* HEADER */}

        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl dark:text-white">
              Image Manager
            </h1>

            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your website images and control when they are displayed.
            </p>
          </div>

          <button
            onClick={openAddModal}
            className="flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
          >
            <Plus size={18} />

            Add New Image
          </button>

        </div>

        {/* STATS */}

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

          <StatCard
            title="Total Images"
            value={totalImages}
            icon={<ImageIcon size={20} />}
          />

          <StatCard
            title="Active"
            value={activeImages}
            icon={<span>●</span>}
          />

          <StatCard
            title="Scheduled"
            value={scheduledImages}
            icon={<Clock size={20} />}
          />

          <StatCard
            title="Disabled"
            value={disabledImages}
            icon={<span>○</span>}
          />

        </div>

        {/* SEARCH */}

        <div className="mb-6 flex items-center rounded-xl border border-gray-200 bg-white px-4 dark:border-gray-800 dark:bg-[#111418]">

          <Search
            size={19}
            className="text-gray-400"
          />

          <input
            type="text"
            placeholder="Search images..."
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            className="w-full bg-transparent px-3 py-3 text-sm outline-none placeholder:text-gray-400"
          />

        </div>

        {/* LOADING */}

        {loading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center dark:border-gray-800 dark:bg-[#111418]">
            <p className="text-sm text-gray-500">
              Loading images...
            </p>
          </div>
        ) : filteredImages.length === 0 ? (

          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-16 text-center dark:border-gray-700 dark:bg-[#111418]">

            <ImageIcon
              size={42}
              className="mx-auto mb-4 text-gray-400"
            />

            <h2 className="font-semibold text-gray-900 dark:text-white">
              No images found
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Add a new image to get started.
            </p>

          </div>

        ) : (

          /* IMAGE GRID */

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">

            {filteredImages.map(
              (image) => (

                <div
                  key={image.id}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-[#111418]"
                >

                  {/* IMAGE */}

                  <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">

                    <img
                      src={image.imageUrl}
                      alt={image.title}
                      className="h-full w-full object-cover transition duration-300 hover:scale-105"
                    />

                    <div className="absolute left-3 top-3">
                      <StatusBadge
                        status={
                          image.status
                        }
                      />
                    </div>

                  </div>

                  {/* CONTENT */}

                  <div className="p-5">

                    <div className="mb-5 flex items-start justify-between gap-3">

                      <div>
                        <h2 className="font-semibold text-gray-900 dark:text-white">
                          {image.title}
                        </h2>

                        <p className="mt-1 text-xs text-gray-400">
                          Image ID:{" "}
                          {image.id}
                        </p>
                      </div>

                      {/* TOGGLE */}

                      <button
                        onClick={() =>
                          toggleStatus(
                            image
                          )
                        }
                        className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                          image.enabled
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-gray-700"
                        }`}
                      >
                        <span
                          className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${
                            image.enabled
                              ? "left-6"
                              : "left-1"
                          }`}
                        />
                      </button>

                    </div>

                    {/* SCHEDULE */}

                    <div className="space-y-3 rounded-xl bg-gray-50 p-4 dark:bg-[#191d22]">

                      <div className="flex gap-3">

                        <CalendarDays
                          size={17}
                          className="mt-0.5 shrink-0 text-gray-400"
                        />

                        <div>
                          <p className="text-xs text-gray-400">
                            Show from
                          </p>

                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {formatDate(
                              image.startAt
                            )}{" "}
                            at{" "}
                            {formatTime(
                              image.startAt
                            )}
                          </p>
                        </div>

                      </div>

                      <div className="flex gap-3">

                        <Clock
                          size={17}
                          className="mt-0.5 shrink-0 text-gray-400"
                        />

                        <div>
                          <p className="text-xs text-gray-400">
                            Show until
                          </p>

                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {formatDate(
                              image.endAt
                            )}{" "}
                            at{" "}
                            {formatTime(
                              image.endAt
                            )}
                          </p>
                        </div>

                      </div>

                    </div>

                    {/* ACTIONS */}

                    <div className="mt-4 flex gap-2">

                      <button
                        onClick={() =>
                          openEditModal(
                            image
                          )
                        }
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <Edit3 size={16} />

                        Edit
                      </button>

                      <button
                        onClick={() =>
                          deleteImage(
                            image
                          )
                        }
                        className="flex items-center justify-center rounded-xl bg-red-50 px-4 py-2.5 text-red-600 transition hover:bg-red-100 dark:bg-red-950/40 dark:hover:bg-red-950"
                      >
                        <Trash2 size={17} />
                      </button>

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        )}

      </div>

      {/* MODAL */}

      {showModal && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">

          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-[#111418]">

            {/* MODAL HEADER */}

            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 dark:border-gray-800">

              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingImage
                    ? "Edit Image"
                    : "Add New Image"}
                </h2>

                <p className="mt-1 text-sm text-gray-500">
                  Upload once and control the display schedule.
                </p>
              </div>

              <button
                onClick={closeModal}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white"
              >
                <X size={20} />
              </button>

            </div>

            {/* FORM */}

            <div className="space-y-6 p-6">

              {/* IMAGE */}

              <div>

                <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                  Image
                </label>

                {preview ? (

                  <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">

                    <div className="aspect-video">
                      <img
                        src={preview}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <label className="absolute bottom-3 right-3 flex cursor-pointer items-center gap-2 rounded-lg bg-black/80 px-4 py-2 text-sm font-medium text-white backdrop-blur">

                      <Upload size={16} />

                      Change Image

                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={
                          handleImageChange
                        }
                      />

                    </label>

                  </div>

                ) : (

                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 p-10 transition hover:border-gray-500 dark:border-gray-700 dark:hover:border-gray-500">

                    <Upload
                      size={32}
                      className="mb-3 text-gray-400"
                    />

                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Click to upload image
                    </p>

                    <p className="mt-1 text-xs text-gray-400">
                      PNG, JPG, JPEG or WEBP
                    </p>

                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={
                        handleImageChange
                      }
                    />

                  </label>

                )}

              </div>

              {/* TITLE */}

              <div>

                <label className="mb-2 block text-sm font-semibold">
                  Image Title
                </label>

                <input
                  type="text"
                  value={form.title}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      title:
                        event.target.value,
                    })
                  }
                  placeholder="Example: Homepage Summer Banner"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-black dark:border-gray-700 dark:bg-gray-900 dark:focus:border-white"
                />

              </div>

              {/* DATE / TIME */}

              <div className="grid gap-5 md:grid-cols-2">

                <div>
                  <label className="mb-2 block text-sm font-semibold">
                    Start Date
                  </label>

                  <input
                    type="date"
                    value={
                      form.startDate
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        startDate:
                          event.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">
                    Start Time
                  </label>

                  <input
                    type="time"
                    value={
                      form.startTime
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        startTime:
                          event.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">
                    End Date
                  </label>

                  <input
                    type="date"
                    value={
                      form.endDate
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        endDate:
                          event.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold">
                    End Time
                  </label>

                  <input
                    type="time"
                    value={
                      form.endTime
                    }
                    onChange={(event) =>
                      setForm({
                        ...form,
                        endTime:
                          event.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>

              </div>

              {/* ENABLE */}

              <div className="flex items-center justify-between rounded-xl bg-gray-50 p-4 dark:bg-[#191d22]">

                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Enable Image
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    Turn this off if you don't want the image displayed.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      enabled:
                        !form.enabled,
                    })
                  }
                  className={`relative h-6 w-11 rounded-full transition ${
                    form.enabled
                      ? "bg-green-500"
                      : "bg-gray-300 dark:bg-gray-700"
                  }`}
                >
                  <span
                    className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${
                      form.enabled
                        ? "left-6"
                        : "left-1"
                    }`}
                  />
                </button>

              </div>

            </div>

            {/* FOOTER */}

            <div className="flex gap-3 border-t border-gray-200 px-6 py-5 dark:border-gray-800">

              <button
                onClick={closeModal}
                disabled={saving}
                className="flex-1 rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 rounded-xl bg-black px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-gray-200"
              >
                {saving
                  ? "Saving..."
                  : editingImage
                    ? "Update Image"
                    : "Save Image"}
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

function StatCard({
  title,
  value,
  icon,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-[#111418]">

      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
        {icon}
      </div>

      <div>
        <p className="text-xs font-medium text-gray-500">
          {title}
        </p>

        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
          {value}
        </p>
      </div>

    </div>
  );
}

/* =========================================================
   STATUS BADGE
========================================================= */

function StatusBadge({
  status,
}: {
  status: ImageStatus;
}) {
  const styles = {
    Active:
      "bg-green-500 text-white",

    Scheduled:
      "bg-blue-500 text-white",

    Expired:
      "bg-gray-500 text-white",

    Disabled:
      "bg-red-500 text-white",
  };

  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold shadow ${styles[status]}`}
    >
      {status}
    </span>
  );
}